import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Environment, type Workspace } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import { useConfirm } from "../components/ConfirmDialog";

// Curated environment colors. Picked to be easily distinguishable + match
// the rest of the palette used in status pills and method tags.
const PALETTE: Array<{ name: string; hex: string }> = [
  { name: "Red",     hex: "#ef4444" },
  { name: "Amber",   hex: "#f59e0b" },
  { name: "Yellow",  hex: "#eab308" },
  { name: "Green",   hex: "#22c55e" },
  { name: "Teal",    hex: "#14b8a6" },
  { name: "Cyan",    hex: "#06b6d4" },
  { name: "Sky",     hex: "#0ea5e9" },
  { name: "Blue",    hex: "#3b82f6" },
  { name: "Indigo",  hex: "#6366f1" },
  { name: "Violet",  hex: "#8b5cf6" },
  { name: "Pink",    hex: "#ec4899" },
  { name: "Slate",   hex: "#64748b" },
];

export default function EnvironmentsPage() {
  const { workspace } = useOutletContext<{ workspace: Workspace }>();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const nav = useNavigate();
  const params = useParams();
  const routeEnvId = params.envId ?? null;

  const list = useQuery({
    queryKey: ["environments", workspace.id],
    queryFn: () => api.environments.list(workspace.id),
  });
  const envs = list.data?.environments ?? [];

  const selectedId = routeEnvId ?? envs[0]?.id ?? null;
  // If the URL doesn't include an env yet and we have one, redirect so the
  // location bar always shows the deep-linkable form.
  useEffect(() => {
    if (!routeEnvId && envs.length) nav(`/environments/${envs[0].id}`, { replace: true });
  }, [routeEnvId, envs, nav]);
  const setSelectedId = (id: string | null) => {
    if (id) nav(`/environments/${id}`);
    else nav(`/environments`);
  };
  const selected = useMemo(() => envs.find((e) => e.id === selectedId) ?? null, [envs, selectedId]);

  const create = useMutation({
    mutationFn: (body: { name: string; color: string | null }) => api.environments.create({ workspace_id: workspace.id, name: body.name, color: body.color }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["environments", workspace.id] }); setSelectedId(r.id); },
  });

  return (
    <div className="h-full flex min-h-0">
      <aside className="w-64 shrink-0 border-r border-border bg-surface flex flex-col min-h-0">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Environments</div>
        </div>
        <div className="flex-1 overflow-y-auto scroll-pane py-1">
          {envs.length === 0 && <p className="px-3 py-2 text-xs muted italic">None yet. Add one below.</p>}
          <ul>
            {envs.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(e.id)}
                  className={
                    "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left " +
                    (e.id === selectedId ? "bg-accent-soft text-accent-bright" : "text-text-dim hover:bg-surface-2 hover:text-text")
                  }
                >
                  <EnvDot color={e.color} />
                  <span className="flex-1 truncate">{e.name}</span>
                  {e.is_default && <span className="chip bg-surface-3 muted-strong text-[10px]">default</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <NewEnvForm pending={create.isPending} onCreate={(name, color) => create.mutate({ name, color })} />
      </aside>

      <main className="flex-1 min-w-0 overflow-auto scroll-pane bg-bg p-6">
        {selected ? (
          <EnvEditor key={selected.id} env={selected} workspaceId={workspace.id} onDeleted={() => { setSelectedId(null); qc.invalidateQueries({ queryKey: ["environments", workspace.id] }); }} confirm={confirm} />
        ) : (
          <div className="grid h-full place-items-center muted">
            <div className="text-center">
              <p className="text-sm">Pick or create an environment to manage its variables.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export function NewEnvForm({ pending, onCreate }: { pending: boolean; onCreate: (name: string, color: string | null) => void }) {
  const [name, setName] = useState("Production");
  const [color, setColor] = useState<string | null>(PALETTE[0].hex);
  return (
    <form
      className="border-t border-border p-2 space-y-2"
      onSubmit={(e) => { e.preventDefault(); if (name.trim()) onCreate(name.trim(), color); }}
    >
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New environment name" />
      <ColorRow value={color} onChange={setColor} />
      <LoadingButton type="submit" className="btn-primary w-full justify-center" pending={pending}>
        Create
      </LoadingButton>
    </form>
  );
}

export function EnvEditor({ env, workspaceId, onDeleted, onDuplicated, confirm, highlightVar }: {
  env: Environment;
  workspaceId: string;
  onDeleted: () => void;
  onDuplicated?: (newId: string) => void;
  confirm: (opts: { title: string; description?: string; danger?: boolean; confirmLabel?: string }) => Promise<boolean>;
  // When set, the editor scrolls the matching row into view and flashes
  // a transient highlight ring. Used by the click-on-chip flow.
  highlightVar?: string;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(env.name);
  const [color, setColor] = useState<string | null>(env.color);
  const [isDefault, setIsDefault] = useState(env.is_default);
  const [vars, setVars] = useState<Array<{ key: string; value: string }>>(
    Object.entries(env.vars).map(([key, value]) => ({ key, value })),
  );

  const dirty = useMemo(() => {
    if (env.name !== name) return true;
    if (env.color !== color) return true;
    if (env.is_default !== isDefault) return true;
    const reconstructed = Object.fromEntries(vars.filter((kv) => kv.key).map((kv) => [kv.key, kv.value]));
    return JSON.stringify(reconstructed) !== JSON.stringify(env.vars);
  }, [env, name, color, isDefault, vars]);

  const save = useMutation({
    mutationFn: () => api.environments.update(env.id, {
      name: name.trim() || env.name,
      vars: Object.fromEntries(vars.filter((kv) => kv.key).map((kv) => [kv.key, kv.value])),
      color,
      is_default: isDefault,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["environments", workspaceId] }),
  });

  const remove = useMutation({
    mutationFn: () => api.environments.remove(env.id),
    onSuccess: onDeleted,
  });

  // Duplicate — copies the env's current name + vars + color into a
  // fresh row. The user might already have edits in flight, so we
  // duplicate from the SAVED env (the prop) rather than the local
  // state — otherwise they'd silently lose their unsaved tweaks.
  // is_default is intentionally not carried over: there can only be
  // one default at a time.
  const duplicate = useMutation({
    mutationFn: () => api.environments.create({
      workspace_id: workspaceId,
      name: `${env.name} copy`,
      vars: env.vars,
      color: env.color,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["environments", workspaceId] });
      onDuplicated?.(r.id);
    },
  });

  // Rows are exactly what the user has typed — no trailing empty row. A
  // dedicated "+ Add variable" button below adds one. Less noise.
  const rows = vars;

  // Highlight + scroll the row matching `highlightVar` when the user
  // clicked a {{NAME}} chip. We also auto-create the row when it
  // doesn't exist yet, so clicking an undefined variable becomes a
  // shortcut to "define it here".
  const rowRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [flashedKey, setFlashedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!highlightVar) return;
    const idx = rows.findIndex((r) => r.key === highlightVar);
    if (idx >= 0) {
      // Defer a tick so the rows are mounted before we measure.
      requestAnimationFrame(() => {
        rowRefs.current.get(idx)?.scrollIntoView({ block: "center", behavior: "smooth" });
        setFlashedKey(highlightVar);
        window.setTimeout(() => setFlashedKey(null), 1_400);
      });
    } else {
      // Var doesn't exist in this env yet — append a pre-filled row so
      // the click is a one-step "let me define this".
      setVars((prev) => prev.some((r) => r.key === highlightVar)
        ? prev
        : [...prev, { key: highlightVar, value: "" }]
      );
      setFlashedKey(highlightVar);
      window.setTimeout(() => setFlashedKey(null), 1_400);
    }
  }, [highlightVar]); // eslint-disable-line react-hooks/exhaustive-deps
  const setRow = (i: number, patch: Partial<{ key: string; value: string }>) => {
    setVars(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => setVars(rows.filter((_, idx) => idx !== i));
  const addRow = () => setVars([...rows, { key: "", value: "" }]);

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <EnvDot color={color} size={18} />
        <h1 className="text-2xl font-semibold tracking-tight">{env.name}</h1>
        {env.is_personal && (
          <span className="chip bg-accent-soft text-accent-bright inline-flex items-center gap-1" title="Personal — only visible to you">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M7 11V8a5 5 0 0110 0v3M5 11h14v9H5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            personal
          </span>
        )}
        {env.is_default && <span className="chip bg-surface-3 muted-strong">default</span>}
        <LoadingButton
          className="btn ml-auto"
          pending={duplicate.isPending}
          onClick={() => duplicate.mutate()}
          title="Create a new environment with the same variables"
        >
          Duplicate
        </LoadingButton>
      </div>

      <form
        className="card p-5 space-y-4"
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      >
        <h2 className="font-semibold">Details</h2>
        <div className="space-y-2">
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="space-y-2">
          <label className="label">Color</label>
          <p className="text-xs muted-strong">
            Shown next to the environment name everywhere (request bar, runner, command palette) so you can tell at a glance which environment you're hitting.
          </p>
          <ColorRow value={color} onChange={setColor} />
        </div>

        {!env.is_personal && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <span>Make this the default environment for the workspace</span>
          </label>
        )}
        {env.is_personal && (
          <p className="text-xs muted">
            This environment is <strong>personal</strong> — its variables aren't shared with anyone else in the workspace, and runs triggered by other teammates can't resolve its values. It can't be set as the workspace default.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <LoadingButton type="submit" className="btn-primary" pending={save.isPending} disabled={!dirty}>
            Save
          </LoadingButton>
        </div>
      </form>

      <div className="card p-5 space-y-3">
        <h2 className="font-semibold">Variables</h2>
        <p className="text-sm muted">Reference these from any request, header, body, or script with <code>{`{{name}}`}</code>.</p>
        <div className="space-y-1">
          {rows.length > 0 && (
            <div className="grid grid-cols-[1fr_1fr_28px] gap-2 text-[11px] uppercase tracking-wider muted-strong px-1">
              <div>Name</div><div>Value</div><div></div>
            </div>
          )}
          {rows.map((row, i) => {
            const flashed = !!flashedKey && row.key === flashedKey;
            return (
              <div
                key={i}
                ref={(el) => { rowRefs.current.set(i, el); }}
                className={
                  "grid grid-cols-[1fr_1fr_28px] gap-2 rounded p-1 -m-1 transition-colors " +
                  (flashed ? "bg-accent-soft ring-1 ring-accent" : "")
                }
              >
                <input className="input input-mono" value={row.key} onChange={(e) => setRow(i, { key: e.target.value })} placeholder="API_HOST" />
                <input className="input input-mono" value={row.value} onChange={(e) => setRow(i, { value: e.target.value })} placeholder="https://api.example.com" autoFocus={flashed && !row.value} />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2"
                  title="Remove"
                  aria-label="Remove variable"
                >✕</button>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="empty-card">No variables yet. Click <span className="empty-card-strong">+ Add variable</span> to define one.</div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <button type="button" className="btn" onClick={addRow}>＋ Add variable</button>
          <LoadingButton className="btn-primary" pending={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
            Save variables
          </LoadingButton>
        </div>
      </div>

      <div className="card p-5 space-y-3 border-danger/40">
        <h2 className="font-semibold text-danger">Danger zone</h2>
        <p className="text-sm muted">Deleting an environment is permanent. Any request that was using its <code>{`{{var}}`}</code> values will fall back to an empty string.</p>
        <div>
          <LoadingButton
            className="btn-danger"
            pending={remove.isPending}
            onClick={async () => {
              const ok = await confirm({ title: `Delete "${env.name}"?`, danger: true, confirmLabel: "Delete" });
              if (ok) remove.mutate();
            }}
          >
            Delete environment
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

function ColorRow({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={
          "h-8 w-8 rounded-full grid place-items-center transition " +
          (value == null
            ? "ring-2 ring-text-faint bg-surface-2"
            : "bg-surface-2 hover:bg-surface-3 opacity-70 hover:opacity-100")
        }
        title="No color"
        aria-label="No color"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-text-faint"><line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
      </button>
      {PALETTE.map((p) => {
        const active = value?.toLowerCase() === p.hex.toLowerCase();
        return (
          <button
            key={p.hex}
            type="button"
            onClick={() => onChange(p.hex)}
            // Selected swatch: ring in the swatch's own color + a clear
            // checkmark over the fill. No more confusing purple border.
            className={
              "h-8 w-8 rounded-full grid place-items-center transition " +
              (active ? "ring-2 ring-offset-2 ring-offset-surface" : "hover:scale-110")
            }
            style={{
              background: p.hex,
              ["--tw-ring-color" as string]: active ? p.hex : undefined,
            }}
            title={p.name}
            aria-label={p.name}
            aria-pressed={active}
          >
            {active && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4 10-10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function EnvDot({ color, size = 10, className = "" }: { color: string | null | undefined; size?: number; className?: string }) {
  if (!color) {
    return (
      <span
        className={"inline-block rounded-full border border-border-strong " + className}
        style={{ width: size, height: size, background: "var(--surface-3)" }}
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={"inline-block rounded-full " + className}
      style={{ width: size, height: size, background: color, boxShadow: `0 0 0 1px rgb(0 0 0 / 0.25) inset` }}
      aria-hidden="true"
    />
  );
}
